import { useContext, useEffect, useRef } from 'react';
import type { LiveAvatarTTSResponse } from '@trinity/chat/src/types/heygen';
import type { TTSConfig } from '@trinity/chat/src/types/tts-config';
import { ChatLoggerContext } from '../../containers/Chat/contexts.js';
import { useChatApiClient } from '../../containers/Chat/useApiClient.js';
import { useLiveAvatarContextOptional } from './LiveAvatar.context';

type Props = {
  /**
   * Text the avatar will speak once the HeyGen session stream is ready.
   * Defaults to a generic greeting if not provided.
   */
  sampleText: string;
  /**
   * TTS provider configuration to use for synthesis.
   * If null, the bridge will silently no-op (avatar appears but is silent).
   */
  ttsConfig: TTSConfig | null;
};

/**
 * AvatarPreviewBridge
 *
 * Renderless component (returns null) used inside the Avatar Preview modal.
 * Waits until the HeyGen session stream is ready (`isStreamReady`), then
 * calls the backend TTS endpoint once and drives the avatar to speak a
 * sample sentence.
 *
 * This is a simplified version of HeyGenResponseBridge that:
 * - Does NOT depend on ActiveChatSessionContext or ChatSessionQueriesContext
 * - Fires exactly once per mount (guarded by a ref flag)
 * - Accepts the text and ttsConfig as explicit props
 *
 * Must be rendered as a descendant of LiveAvatarSessionManager.
 */
export function AvatarPreviewBridge({ sampleText, ttsConfig }: Props): null {
  const ctx = useLiveAvatarContextOptional();
  const apiClient = useChatApiClient();
  const loggerCtx = useContext(ChatLoggerContext);

  // Guard: fire TTS at most once per mount, regardless of re-renders.
  const hasFiredRef = useRef(false);

  useEffect(() => {
    // Wait until the stream is actually ready before sending audio.
    if (!ctx?.isStreamReady) {
      return;
    }

    // Session ref must be populated.
    if (!ctx.sessionRef.current) {
      return;
    }

    // No TTS config — avatar is visible but silent.
    if (!ttsConfig) {
      return;
    }

    // Only fire once per mount.
    if (hasFiredRef.current) {
      return;
    }
    hasFiredRef.current = true;

    apiClient.authenticatedClient
      .post<LiveAvatarTTSResponse>('/chat/live-avatar/tts', {
        text: sampleText,
        ttsConfig,
      })
      .then(res => {
        const sessionAtResponse = ctx.sessionRef.current;

        if (!sessionAtResponse) {
          // Session was stopped while the request was in-flight.
          return;
        }

        // Pass the base64 string directly — the HeyGen SDK decodes base64 → binary PCM
        // internally. Calling atob() here would double-decode and produce silence.
        try {
          sessionAtResponse.repeatAudio(res.data.audio);
        } catch (repeatErr: unknown) {
          loggerCtx.captureException(repeatErr);
        }
      })
      .catch((err: unknown) => {
        loggerCtx.captureException(err);
      });
  }, [ctx?.isStreamReady, sampleText, ttsConfig, apiClient, loggerCtx]);

  return null;
}
